import React, { useState, useContext } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { showErrorSnackbar } from '../features/snackbar/snackbarSlice';
import { IPurchaserInfo, IRealtorInfo } from '../types/CreateDealForm';
import { UnitContext } from './UnitContext';
import { ILease, IListingParking, IRoom, ITotalRoom, IWashroom } from '../types/lease';
import { IUtility } from '../components/unitMain/leasing/Tenancy';
import { IDocuments } from '../types/docusign';
import { showSuccessSnackbar } from '../features/snackbar/snackbarSlice';
import { selectProject } from '../features/project/projectSlice';
import { IUser } from '../types/user';
import { numToCurrency } from '../utils/Functions';
import { IMerge, IMergeData } from '../types/merge';

const LeaseContext = React.createContext<any>(null);

const LeaseProvider = (props: any) => {
  const project = useSelector(selectProject);
  const { unit } = useContext(UnitContext);
  const storeDispatch = useAppDispatch();
  const [lease, setLease] = useState<ILease | null>(null);
  const [leases, setLeases] = useState([]);
  const [agreementType, setAgreementType] = useState<string>('');
  const [mergeTemplates, setMergeTemplates] = useState<IMerge[]>([]);

  useQuery<IMergeData>(GETMERGE, {
    variables: {
      filter: {
        OR: [{ type: 'Tenancy' }, { type: 'Management' }, { type: 'Listing' }, { type: 'Leasing' }],
      },
    },
    onCompleted: (data) => {
      setMergeTemplates(data.mergeTemplateMany);
    },
  });

  const { loading } = useQuery(LEASES, {
    variables: { filter: { unit: unit._id } },
    onCompleted: (data) => {
      if (data.leaseMany.length) {
        let activeLease = data.leaseMany.find((lease: ILease) => lease.active);
        if (activeLease) {
          setLease(activeLease);
          setAgreementType('summary');
        }
      }
      setLeases(data.leaseMany);
    },
  });

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createManagement] = useMutation(CREATEMANAGEMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Management Agreement has been created'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateManagement] = useMutation(UPDATEMANAGEMENT, {
    onCompleted: (data) => {
      setLease({
        ...lease!,
        management: data.managementUpdateById.record,
      });
      storeDispatch(showSuccessSnackbar('Management Agreement has been updated'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [removeManagement] = useMutation(REMOVEMANAGEMENT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createListing] = useMutation(CREATELISTING, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Listing Agreement has been created'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateListing] = useMutation(UPDATELISTING, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [removeListing] = useMutation(REMOVELISTING, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createTenancy] = useMutation(CREATETENANCY, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Tenancy Agreement has been created'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateTenancy] = useMutation(UPDATETENANCY, {
    onCompleted: (data) => {
      setLease({
        ...lease!,
        tenancy: data.tenancyUpdateById.record,
      });
      storeDispatch(showSuccessSnackbar('Tenancy Agreement has been updated'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [removeTenancy] = useMutation(REMOVETENANCY, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createLease] = useMutation(CREATELEASE, {
    onCompleted: (data) => {
      setLease(data.leaseCreateOne.record);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateLease] = useMutation(UPDATELEASE, {
    onCompleted: (data) => {
      setLease(data.leaseUpdateById.record);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const saveManagement = async (
    e: any,
    insurance: boolean,
    options: any[],
    rec: boolean,
    executor: IUser,
    developer: IUser,
    reRentalFee: string,
    inspection: string,
    leaseRenewal: string,
    startupFee: string,
    managementFee: string
  ) => {
    e.preventDefault();
    if (!lease) {
      await createManagement({
        variables: {
          record: {
            project: project._id,
            unit: unit._id,
            insurance: insurance,
            rec: rec,
            options: options,
            reRentalFee,
            inspection,
            leaseRenewal,
            startupFee,
            managementFee,
            executor: executor._id,
            developer: developer ? developer._id : null,
          },
        },
      }).then((res) => {
        createLease({
          variables: {
            record: {
              active: true,
              project: project._id,
              unit: unit._id,
              management: res.data.managementCreateOne.record._id,
              listing: null,
              tenancy: null,
              documents: [],
            },
          },
        }).then((leaseRes) => {
          updateManagement({
            variables: {
              _id: res.data.managementCreateOne.record._id,
              record: {
                lease: leaseRes.data.leaseCreateOne.record._id,
              },
            },
          });
        });
      });
    } else {
      if (lease.management) {
        updateManagement({
          variables: {
            _id: lease.management._id,
            record: {
              project: project._id,
              unit: unit._id,
              insurance: insurance,
              rec: rec,
              options: options,
              reRentalFee,
              inspection,
              leaseRenewal,
              startupFee,
              managementFee,
              executor: executor._id,
              developer: developer ? developer._id : null,
            },
          },
        });
      } else {
        await createManagement({
          variables: {
            record: {
              project: project._id,
              unit: unit._id,
              insurance: insurance,
              rec: rec,
              options: options,
              reRentalFee,
              inspection,
              leaseRenewal,
              startupFee,
              managementFee,
              executor: executor._id,
              developer: developer ? developer._id : null,
            },
          },
        }).then((res) => {
          updateLease({
            variables: {
              _id: lease._id,
              record: {
                management: res.data.managementCreateOne.record._id,
              },
            },
          });
        });
      }
    }
  };

  const deleteManagement = async (lease: ILease) => {
    if (lease && lease.management) {
      removeManagement({
        variables: {
          _id: lease.management._id,
        },
      }).then((res) => {
        updateLease({
          variables: {
            _id: lease._id,
            record: {
              management: null,
            },
          },
        });
        storeDispatch(showSuccessSnackbar('Rental Management Profile has been deleted'));
        setAgreementType('summary');
      });
    }
  };

  const saveListing = async (
    e: any,
    address: string,
    dateStart: Date,
    dateEnd: Date,
    dateAvailable: Date | null,
    amount: string,
    executor: IUser,
    developer: IUser | null,
    listingWords: string,
    listingAmount: string,
    cooperatingWords: string,
    cooperatingAmount: string,
    holdover: string,
    mls: string,
    possessionRemarks: string,
    parkingsState: IListingParking[],
    sqftSource: string,
    balcony: string,
    interiorFeature: string[],
    washroomsState: IWashroom[],
    totalRoomsState: ITotalRoom[],
    roomsState: IRoom[]
  ) => {
    e.preventDefault();
    if (!lease) return storeDispatch(showErrorSnackbar('No Lease Found'));

    let listingValue = listingWords;

    if (listingAmount && listingAmount !== '0') {
      listingValue = `${listingWords} + ${numToCurrency.format(parseFloat(listingAmount))}`;
    }

    let cooperatingValue = cooperatingWords;

    if (cooperatingAmount && cooperatingAmount !== '0') {
      cooperatingValue = `${cooperatingWords} + ${numToCurrency.format(parseFloat(cooperatingAmount))}`;
    }

    if (lease.listing) {
      updateListing({
        variables: {
          _id: lease.listing._id,
          record: {
            project: project._id,
            unit: unit._id,
            dateStart: new Date(dateStart).setHours(5, 0, 0, 0),
            dateEnd: new Date(dateEnd).setHours(5, 0, 0, 0),
            dateAvailable: dateAvailable ? new Date(dateAvailable).setHours(5, 0, 0, 0) : null,
            amount: parseFloat(amount),
            executor: executor._id,
            developer: developer ? developer._id : null,
            listing: listingValue,
            cooperating: cooperatingValue,
            holdover: parseInt(holdover, 10),
            mls: mls,
            possessionRemarks,
            parking: parkingsState,
            sqftSource,
            balcony,
            interiorFeatures: interiorFeature,
            washrooms: washroomsState,
            totalRooms: totalRoomsState,
            rooms: roomsState,
          },
        },
      }).then((res) => {
        updateLease({
          variables: {
            _id: lease._id,
            record: {
              address: address,
            },
          },
        });
        setLease({
          ...lease!,
          address: address,
          listing: res.data.listingUpdateById.record,
        });
        storeDispatch(showSuccessSnackbar('Listing Agreement has been updated'));
      });
    } else {
      await createListing({
        variables: {
          record: {
            project: project._id,
            unit: unit._id,
            lease: lease._id,
            dateStart: new Date(dateStart).setHours(5, 0, 0, 0),
            dateEnd: new Date(dateEnd).setHours(5, 0, 0, 0),
            dateAvailable: dateAvailable ? new Date(dateAvailable).setHours(5, 0, 0, 0) : null,
            amount: parseFloat(amount),
            executor: executor._id,
            developer: developer ? developer._id : null,
            listing: listingValue,
            cooperating: cooperatingValue,
            holdover: parseInt(holdover, 10),
            mls: mls,
            possessionRemarks,
            parking: parkingsState,
            sqftSource,
            balcony,
            interiorFeatures: interiorFeature,
            washrooms: washroomsState,
            totalRooms: totalRoomsState,
            rooms: roomsState,
          },
          lease: lease._id,
          address: address,
        },
      }).then((res) => {
        setLease({
          ...lease,
          listing: res.data.createListing,
        });
      });
    }
  };

  const deleteListing = async (lease: ILease) => {
    if (lease && lease.listing) {
      removeListing({
        variables: {
          _id: lease.listing._id,
        },
      }).then((res) => {
        updateLease({
          variables: {
            _id: lease._id,
            record: {
              listing: null,
            },
          },
        });
        storeDispatch(showSuccessSnackbar('Listing Profile has been deleted'));
        setAgreementType('summary');
      });
    }
  };

  const saveTenancy = async (
    e: any,
    tenants: IPurchaserInfo[],
    realtor: IRealtorInfo | null = null,
    tenancyType: string,
    tenancyDateStart: Date | null,
    tenancyDateEnd: Date | null,
    amount: string,
    payableTo: string,
    paymentDay: string,
    paymentType: string,
    paymentMethod: string,
    partialRentAmount: string,
    partialRentPaymentDate: Date | null,
    partialRentStartDate: Date | null,
    partialRentEndDate: Date | null,
    utilitiesState: IUtility[],
    parkingDescription: string,
    lockerDescription: string,
    electricity: string,
    heat: string,
    water: string,
    rentDiscount: string,
    rentDepositAmount: string,
    keyDepositAmount: string,
    keyDeposit: string,
    smokingRules: boolean,
    smokingRulesDescription: string
  ) => {
    e.preventDefault();
    if (!tenants.length) {
      return storeDispatch(showErrorSnackbar('No Tenants found'));
    }
    if (!tenants.every((purchaser: IPurchaserInfo) => purchaser._id)) {
      return storeDispatch(showErrorSnackbar('A Tenant has not been saved'));
    }
    if (!utilitiesState.every((utility) => utility.answer.length))
      return storeDispatch(showErrorSnackbar('Every utility needs at least one selected option'));

    if (lease && lease.tenancy) {
      updateTenancy({
        variables: {
          _id: lease.tenancy._id,
          record: {
            project: project._id,
            unit: unit._id,
            tenants: tenants.map((purchaser: IPurchaserInfo) => purchaser._id),
            realtor: realtor ? realtor._id : null,
            tenancyType: tenancyType,
            dateStart: tenancyDateStart ? new Date(tenancyDateStart).setHours(5, 0, 0, 0) : null,
            dateEnd: tenancyDateEnd ? new Date(tenancyDateEnd).setHours(5, 0, 0, 0) : null,
            parkingDescription: parkingDescription,
            lockerDescription: lockerDescription,
            payableTo: payableTo,
            paymentDay: paymentDay,
            paymentType: paymentType,
            paymentMethod: paymentMethod,
            partialRentAmount: partialRentAmount ? parseFloat(partialRentAmount) : 0,
            partialRentPaymentDate: partialRentPaymentDate,
            partialRentStartDate: partialRentStartDate,
            partialRentEndDate: partialRentEndDate,
            amount: parseFloat(amount),
            utilities: utilitiesState.map((utility: IUtility) => {
              return {
                name: utility.name,
                answer: utility.answer,
              };
            }),
            electricity: electricity,
            heat: heat,
            water: water,
            rentDiscount: rentDiscount,
            rentDepositAmount: rentDepositAmount ? parseFloat(rentDepositAmount) : 0,
            keyDepositAmount: keyDepositAmount ? parseFloat(keyDepositAmount) : 0,
            keyDeposit: keyDeposit,
            smokingRules: smokingRules,
            smokingRulesDescription: smokingRulesDescription,
          },
        },
      });
    } else {
      await createTenancy({
        variables: {
          record: {
            project: project._id,
            unit: unit._id,
            tenants: tenants.map((purchaser: IPurchaserInfo) => purchaser._id),
            realtor: realtor ? realtor._id : null,
            tenancyType: tenancyType,
            dateStart: tenancyDateStart ? new Date(tenancyDateStart).setHours(5, 0, 0, 0) : null,
            dateEnd: tenancyDateEnd ? new Date(tenancyDateEnd).setHours(5, 0, 0, 0) : null,
            parkingDescription: parkingDescription,
            lockerDescription: lockerDescription,
            payableTo: payableTo,
            paymentDay: paymentDay,
            paymentType: paymentType,
            paymentMethod: paymentMethod,
            partialRentAmount: partialRentAmount ? parseFloat(partialRentAmount) : 0,
            partialRentPaymentDate: partialRentPaymentDate,
            partialRentStartDate: partialRentStartDate,
            partialRentEndDate: partialRentEndDate,
            amount: parseFloat(amount),
            utilities: utilitiesState.map((utility: IUtility) => {
              return {
                name: utility.name,
                answer: utility.answer,
              };
            }),
            electricity: electricity,
            heat: heat,
            water: water,
            rentDiscount: rentDiscount,
            rentDepositAmount: rentDepositAmount ? parseFloat(rentDepositAmount) : 0,
            keyDepositAmount: keyDepositAmount ? parseFloat(keyDepositAmount) : 0,
            keyDeposit: keyDeposit,
            smokingRules: smokingRules,
            smokingRulesDescription: smokingRulesDescription,
          },
        },
      }).then((res) => {
        updateLease({
          variables: {
            _id: lease?._id,
            record: {
              tenancy: res.data.tenancyCreateOne.record._id,
            },
          },
        });
      });
    }
  };

  const deleteTenancy = async (lease: ILease) => {
    if (lease && lease.tenancy) {
      removeTenancy({
        variables: {
          _id: lease.tenancy._id,
        },
      }).then((res) => {
        updateLease({
          variables: {
            _id: lease._id,
            record: {
              listing: null,
            },
          },
        });
        storeDispatch(showSuccessSnackbar('Tenancy Profile has been deleted'));
        setAgreementType('summary');
      });
    }
  };

  const handleDocuments = (documents: IDocuments[]) => {
    if (lease) {
      setLease({
        ...lease,
        documents: documents,
      });
    }
  };

  return !loading ? (
    <LeaseContext.Provider
      value={{
        lease,
        setLease,
        leases,
        setLeases,
        saveManagement,
        deleteManagement,
        agreementType,
        setAgreementType,
        handleDocuments,
        saveListing,
        deleteListing,
        saveTenancy,
        deleteTenancy,
        updateLease,
        createLease,
        updateUnit,
        mergeTemplates,
        updateListing,
      }}
    >
      {props.children}
    </LeaseContext.Provider>
  ) : null;
};

const CREATETENANCY = gql`
  mutation tenancyCreateOne($record: CreateOneTenancyInput!) {
    tenancyCreateOne(record: $record) {
      record {
        _id
        tenants {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
        }
        tenancyType
        dateStart
        dateEnd
        parkingDescription
        lockerDescription
        payableTo
        paymentDay
        paymentType
        paymentMethod
        partialRentAmount
        partialRentPaymentDate
        partialRentStartDate
        partialRentEndDate
        amount
        utilities {
          name
          answer
        }
        electricity
        heat
        water
        rentDiscount
        rentDepositAmount
        keyDepositAmount
        keyDeposit
        smokingRules
        smokingRulesDescription
      }
    }
  }
`;

const CREATEMANAGEMENT = gql`
  mutation managementCreateOne($record: CreateOneManagementInput!) {
    managementCreateOne(record: $record) {
      record {
        _id
        options
        rec
        insurance
        reRentalFee
        inspection
        leaseRenewal
        startupFee
        managementFee
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
      }
    }
  }
`;

const CREATELISTING = gql`
  mutation createListing($record: CreateOneListingInput!, $lease: MongoID!, $address: String) {
    createListing(record: $record, lease: $lease, address: $address) {
      _id
      dateStart
      dateEnd
      amount
      listing
      cooperating
      developer {
        _id
        fullName
      }
      executor {
        _id
        fullName
      }
      holdover
      dateAvailable
      mls
      possessionRemarks
      parking {
        space
        type
        count
      }
      sqftSource
      totalRooms {
        name
        count
        additional
      }
      washrooms {
        count
        pieces
        level
      }
      interiorFeatures
      rooms {
        level
        name
        length
        width
        height
        description1
        description2
        description3
      }
      balcony
    }
  }
`;

const CREATELEASE = gql`
  mutation leaseCreateOne($record: CreateOneLeaseInput!) {
    leaseCreateOne(record: $record) {
      record {
        _id
        active
        address
        createdAt
        project {
          _id
        }
        unit {
          _id
        }
        management {
          _id
        }
        listing {
          _id
        }
        landlords {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
        }
        tenancy {
          _id
          tenants {
            _id
            email
            firstName
            lastName
            corp
            sin
            dob
            identifications {
              _id
              name
              getUrl
            }
            streetAddress
            city
            province
            country
            postalCode
            occupation
            employer
            directors
            signingOfficers {
              fullName
              dob
              sin
              primaryPhone
              streetAddress
              email
            }
            purchaserType
            primaryPhone
            idType
            idNumber
            idExpiry
            unit
            idJurisdiction
            getUrl
            putUrl
          }
          realtor {
            _id
            email
            firstName
            lastName
            brokerage
            streetAddress
            city
            province
            country
            postalCode
            brokeragePhone
            brokerageFax
            directPhone
          }
          tenancyType
          dateStart
          dateEnd
          parkingDescription
          lockerDescription
          payableTo
          paymentDay
          paymentType
          paymentMethod
          partialRentAmount
          partialRentPaymentDate
          partialRentStartDate
          partialRentEndDate
          amount
          electricity
          heat
          water
          rentDiscount
          rentDepositAmount
          utilities {
            name
            answer
          }
          keyDepositAmount
          keyDeposit
          smokingRules
          smokingRulesDescription
        }
        documents {
          _id
          name
          project {
            _id
          }
          type
          getUrl
          status
          isAPS
          dsEnvelopeId
          mergeTemplate
        }
      }
    }
  }
`;

const LEASES = gql`
  query leaseMany($filter: FilterFindManyLeaseInput) {
    leaseMany(filter: $filter) {
      _id
      active
      address
      payProp
      landlords {
        _id
        email
        firstName
        lastName
        corp
        sin
        dob
        identifications {
          _id
          name
          getUrl
        }
        streetAddress
        city
        province
        country
        postalCode
        occupation
        employer
        directors
        signingOfficers {
          fullName
          dob
          sin
          primaryPhone
          streetAddress
          email
        }
        purchaserType
        primaryPhone
        idType
        idNumber
        idExpiry
        unit
        idJurisdiction
        getUrl
        putUrl
      }
      createdAt
      project {
        _id
      }
      unit {
        _id
      }
      uploads {
        _id
        name
        getUrl
      }
      utilitiesSetup {
        _id
        name
        getUrl
      }
      tenantInsurance {
        _id
        name
        getUrl
      }
      keyHandover {
        _id
        name
        getUrl
      }
      management {
        _id
        options
        rec
        insurance
        reRentalFee
        inspection
        leaseRenewal
        startupFee
        managementFee
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
      }
      listing {
        _id
        dateStart
        dateEnd
        amount
        listing
        cooperating
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
        holdover
        dateAvailable
        mls
        possessionRemarks
        parking {
          space
          type
          count
        }
        sqftSource
        totalRooms {
          name
          count
          additional
        }
        washrooms {
          count
          pieces
          level
        }
        interiorFeatures
        rooms {
          level
          name
          length
          width
          height
          description1
          description2
          description3
        }
        balcony
        pendingAmount
        pendingExpiryDate
      }
      tenancy {
        _id
        tenants {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
        }
        realtor {
          _id
          email
          firstName
          lastName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
        }
        tenancyType
        dateStart
        dateEnd
        parkingDescription
        lockerDescription
        payableTo
        paymentDay
        paymentType
        paymentMethod
        partialRentAmount
        partialRentPaymentDate
        partialRentStartDate
        partialRentEndDate
        amount
        electricity
        heat
        water
        rentDiscount
        rentDepositAmount
        utilities {
          name
          answer
        }
        keyDepositAmount
        keyDeposit
        smokingRules
        smokingRulesDescription
      }
      documents {
        _id
        name
        project {
          _id
        }
        type
        getUrl
        status
        isAPS
        dsEnvelopeId
        mergeTemplate
      }
    }
  }
`;

const UPDATEMANAGEMENT = gql`
  mutation managementUpdateById($_id: MongoID!, $record: UpdateByIdManagementInput!) {
    managementUpdateById(_id: $_id, record: $record) {
      record {
        _id
        options
        rec
        insurance
        reRentalFee
        inspection
        leaseRenewal
        startupFee
        managementFee
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
      }
    }
  }
`;

const UPDATELISTING = gql`
  mutation listingUpdateById($_id: MongoID!, $record: UpdateByIdListingInput!) {
    listingUpdateById(_id: $_id, record: $record) {
      record {
        _id
        dateStart
        dateEnd
        amount
        listing
        cooperating
        executor {
          _id
          fullName
        }
        developer {
          _id
          fullName
        }
        holdover
        dateAvailable
        mls
        possessionRemarks
        parking {
          space
          type
          count
        }
        sqftSource
        totalRooms {
          name
          count
          additional
        }
        washrooms {
          count
          pieces
          level
        }
        interiorFeatures
        rooms {
          level
          name
          length
          width
          height
          description1
          description2
          description3
        }
        balcony
        pendingAmount
        pendingExpiryDate
      }
    }
  }
`;

const UPDATETENANCY = gql`
  mutation tenancyUpdateById($_id: MongoID!, $record: UpdateByIdTenancyInput!) {
    tenancyUpdateById(_id: $_id, record: $record) {
      record {
        _id
        tenants {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
        }
        tenancyType
        dateStart
        dateEnd
        parkingDescription
        lockerDescription
        payableTo
        paymentDay
        paymentType
        paymentMethod
        partialRentAmount
        partialRentPaymentDate
        partialRentStartDate
        partialRentEndDate
        amount
        electricity
        heat
        water
        rentDiscount
        rentDepositAmount
        utilities {
          name
          answer
        }
        keyDepositAmount
        keyDeposit
        smokingRules
        smokingRulesDescription
      }
    }
  }
`;

const UPDATELEASE = gql`
  mutation leaseUpdateById($_id: MongoID!, $record: UpdateByIdLeaseInput!) {
    leaseUpdateById(_id: $_id, record: $record) {
      record {
        _id
        active
        createdAt
        address
        payProp
        project {
          _id
        }
        unit {
          _id
        }
        uploads {
          name
          getUrl
        }
        landlords {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          unit
          idJurisdiction
          getUrl
          putUrl
        }
        documents {
          _id
          name
          project {
            _id
          }
          type
          getUrl
          status
          isAPS
          dsEnvelopeId
          mergeTemplate
        }
        tenancy {
          _id
          tenants {
            _id
            email
            firstName
            lastName
            corp
            sin
            dob
            identifications {
              _id
              name
              getUrl
            }
            streetAddress
            city
            province
            country
            postalCode
            occupation
            employer
            directors
            signingOfficers {
              fullName
              dob
              sin
              primaryPhone
              streetAddress
              email
            }
            purchaserType
            primaryPhone
            idType
            idNumber
            idExpiry
            unit
            idJurisdiction
            getUrl
            putUrl
          }
          realtor {
            _id
            email
            firstName
            lastName
            brokerage
            streetAddress
            city
            province
            country
            postalCode
            brokeragePhone
            brokerageFax
            directPhone
          }
          tenancyType
          dateStart
          dateEnd
          parkingDescription
          lockerDescription
          payableTo
          paymentDay
          paymentType
          paymentMethod
          partialRentAmount
          partialRentPaymentDate
          partialRentStartDate
          partialRentEndDate
          amount
          electricity
          heat
          water
          rentDiscount
          rentDepositAmount
          utilities {
            name
            answer
          }
          keyDepositAmount
          keyDeposit
          smokingRules
          smokingRulesDescription
        }
        management {
          _id
          options
          rec
          insurance
          reRentalFee
          inspection
          leaseRenewal
          startupFee
          managementFee
          executor {
            _id
            fullName
          }
        }
        listing {
          _id
          dateStart
          dateEnd
          amount
          listing
          cooperating
          executor {
            _id
            fullName
          }
          holdover
          dateAvailable
          mls
          possessionRemarks
          parking {
            space
            type
            count
          }
          sqftSource
          totalRooms {
            name
            count
            additional
          }
          washrooms {
            count
            pieces
            level
          }
          interiorFeatures
          rooms {
            level
            name
            length
            width
            height
            description1
            description2
            description3
          }
          balcony
          pendingAmount
          pendingExpiryDate
        }
      }
    }
  }
`;

const REMOVEMANAGEMENT = gql`
  mutation managementRemoveById($_id: MongoID!) {
    managementRemoveById(_id: $_id) {
      record {
        _id
      }
    }
  }
`;

const REMOVELISTING = gql`
  mutation listingRemoveById($_id: MongoID!) {
    listingRemoveById(_id: $_id) {
      record {
        _id
      }
    }
  }
`;

const REMOVETENANCY = gql`
  mutation tenancyRemoveById($_id: MongoID!) {
    tenancyRemoveById(_id: $_id) {
      record {
        _id
      }
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
        leaseStatus
      }
    }
  }
`;

const GETMERGE = gql`
  query mergeTemplateMany($filter: FilterFindManyMergeTemplateInput) {
    mergeTemplateMany(filter: $filter) {
      _id
      name
      mergeFields {
        key
        index
        pageNumber
        x
        y
        fontSize
        format
        wrap
      }
      signFields {
        key
        index
        pageNumber
        x
        y
        title
      }
      type
      getUrl
      putUrl
    }
  }
`;

export { LeaseContext, LeaseProvider };
